/* ──────────────────────────────────────────────────────────────────────────
 * New User Landing Page — post-login home for new / no-data users.
 * Built from the supplied "New User.html" reference. All CTAs wired to real
 * routes; role choices recorded via /api/me/role so returning users land on the
 * right page next time. Multi-role switcher + logout live in the header.
 * Scoped under `.nul`.
 * ────────────────────────────────────────────────────────────────────────── */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import RoleSwitcher from "@/components/RoleSwitcher";
import { setRole } from "@/lib/roleContext";
import { logJourney } from "@/lib/journey";
import "./NewUserLanding.css";

const FAQ_PREVIEW = [
  { cat: "IPO Basics", q: "What exactly is an IPO?" },
  { cat: "IPO Basics", q: "Mainboard vs SME IPO — what's the difference?" },
  { cat: "DRHP & SEBI", q: "What is a DRHP?" },
  { cat: "DRHP & SEBI", q: "How long does the whole IPO process take?" },
  { cat: "Cost", q: "What does an IPO actually cost?" },
  { cat: "Listing", q: "How soon after the issue closes do shares list?" },
  { cat: "Eligibility", q: "Can a loss-making company still IPO?" },
];

const NewUserLanding = () => {
  const navigate = useNavigate();
  const faqRef = useRef(null);
  useEffect(() => { logJourney("Dashboard", "Viewed", "Opened Home Page"); }, []);

  const enterRole = async (role, path) => { await setRole(role); navigate(path); };
  const scrollFaq = (dir) => { if (faqRef.current) faqRef.current.scrollBy({ left: dir * 320, behavior: "smooth" }); };
  // Left-click navigates in-place; right-click / modified-click uses the native
  // anchor so any module can be opened in a new tab (parallel-tab browsing).
  const navClick = (e, to) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
  };
  // Same as navClick but also records the chosen role on a plain left-click.
  const roleClick = (e, role, to) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // native open-in-new-tab
    e.preventDefault();
    enterRole(role, to);
  };

  // Strict rule: on /home the browser Back button does nothing and must NOT log
  // the user out — we simply re-assert the current history entry on popstate.
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const onPop = () => window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  return (
    <div className="nul" data-testid="new-user-landing-page">
      {/* HEADER */}
      <div className="hd">
        <a className="brand" href="/home" onClick={(e) => navClick(e, "/home")} style={{ cursor: "pointer" }} data-testid="nul-brand">IPO<span>Labs</span></a>
        <div className="hnav">
          <a className="partner" href="/reseller-network" onClick={(e) => navClick(e, "/reseller-network")} data-testid="nul-nav-partner">Become our Reseller</a>
          <a className="partner" href="/funding" onClick={(e) => navClick(e, "/funding")} data-testid="nul-nav-funding">Explore Funding Options</a>
        </div>
        <div className="hd-r">
          <a className="hbtn ghost" href="/oracle" onClick={(e) => navClick(e, "/oracle")} data-testid="nul-ask-oracle"><i className="ti ti-robot" aria-hidden="true" /> Ask Oracle {"{IPO.GPT}"}</a>
          <a className="hbtn" href="/ipo-faq" onClick={(e) => navClick(e, "/ipo-faq")} data-testid="nul-ipo-faq"><i className="ti ti-help-circle" aria-hidden="true" /> IPO FAQ&apos;s</a>
          <RoleSwitcher accent="#185FA5" />
        </div>
      </div>

      {/* HERO */}
      <div className="hero">
        <div className="hz" data-testid="hero-free-tools">
          <div className="hz-grid-o" aria-hidden="true" />
          <div className="hz-orb orb1" aria-hidden="true" />
          <div className="hz-orb orb2" aria-hidden="true" />
          <div className="hz-inner">
          <div className="hz-l">
            <span className="hz-kick"><i className="ti ti-sparkles" aria-hidden="true" /> End-to-end IPO management</span>
            <div className="hz-h">From first thought to <span className="em">listing bell</span> — your entire IPO, in one place.</div>
            <div className="hz-d">Built for founders, MSMEs, promoters and CFOs ready to go public. Assemble your advisor team, draft your DRHP, raise capital, and reach the exchange — guided at every step.</div>
            <div className="hz-stats">
              <div><div className="hz-sv">6-stage</div><div className="hz-sl">guided journey</div></div>
              <div className="hz-sd" />
              <div><div className="hz-sv">214+</div><div className="hz-sl">verified advisors</div></div>
              <div className="hz-sd" />
              <div><div className="hz-sv">₹2.4L Cr</div><div className="hz-sl">capital network</div></div>
            </div>
          </div>

          {/* FREE TOOLS */}
          <div className="ft" data-testid="free-tools">
            <div className="ft-h">Try our free tools</div>
            <div className="ft-sub">Explore each module instantly — no setup, no cost.</div>
            {[
              { t: "IPO Costing Platform", d: "Estimate your full IPO cost", ic: "ti-calculator", bg: "#e6f1fb", fg: "#185FA5", to: "/ipo-cost-estimator", tid: "ft-costing" },
              { t: "IPO Readiness Scorecard", d: "Score your SEBI readiness", ic: "ti-gauge", bg: "#e1f5ee", fg: "#0F6E56", to: "/ipo-navigator?screen=readiness", tid: "ft-readiness" },
              { t: "Eligibility Checker", d: "SME vs Main Board", ic: "ti-list-check", bg: "#eef0fb", fg: "#3C3489", to: "/ipo-navigator?screen=readiness&mode=sme", tid: "ft-eligibility" },
              { t: "Business Valuation Toolkit", d: "DCF · NAV · comparable", ic: "ti-chart-pie", bg: "#faeeda", fg: "#854F0B", to: "/ipo-navigator?screen=valuation", tid: "ft-valuation" },
              { t: "Oracle — IPO LLM Platform", d: "Ask anything about IPOs", ic: "ti-robot", bg: "#0c2340", fg: "#5fd39a", to: "/oracle", tid: "ft-oracle" },
            ].map((b) => (
              <a key={b.tid} className="ft-row" href={b.to} onClick={(e) => navClick(e, b.to)} data-testid={b.tid}>
                <div className="ft-ic" style={{ background: b.bg, color: b.fg }}><i className={`ti ${b.ic}`} aria-hidden="true" /></div>
                <div className="ft-tx"><div className="ft-n">{b.t}</div><div className="ft-dd">{b.d}</div></div>
                <i className="ti ti-arrow-right ft-ar" aria-hidden="true" />
              </a>
            ))}
          </div>
          </div>
        </div>
      </div>

      {/* COST & TIMELINE HOOK */}
      <div className="hook">
        <div className="hook-ic"><i className="ti ti-calculator" aria-hidden="true" /></div>
        <div className="hook-t">
          <div className="hook-h">Wondering what an IPO costs — and how long it takes?</div>
          <div className="hook-d">The two questions every founder asks first. Get an instant estimate for your company, free.</div>
        </div>
        <div className="hook-nums">
          <div><div className="hn-v">₹40–90L</div><div className="hn-l">typical SME IPO cost</div></div>
          <div><div className="hn-v">4–6 mo</div><div className="hn-l">to listing</div></div>
        </div>
        <a className="hook-btn" href="/ipo-cost-estimator" onClick={(e) => navClick(e, "/ipo-cost-estimator")} data-testid="nul-estimate">Estimate my IPO Cost →</a>
      </div>

      {/* ROUTER */}
      <div className="sec getstarted">
        <div className="gs-hdr" data-testid="nul-getstarted-hdr">Get Started</div>
        <div className="cards">
          <div className="uc primary">
            <div className="uc-h">I'm a CEO or CA Firm &amp; I want to take my company to IPO</div>
            <div className="uc-d">Manage IPO Process "End-to-End"</div>
            <div className="uc-feat">
              <div className="uc-f"><i className="ti ti-users" style={{ color: "#185FA5" }} aria-hidden="true" /> Hire or Consult IPO Experts</div>
              <div className="uc-f"><i className="ti ti-cash" style={{ color: "#185FA5" }} aria-hidden="true" /> Raise Pre-IPO or Bridge Funds</div>
              <div className="uc-f"><i className="ti ti-file-text" style={{ color: "#185FA5" }} aria-hidden="true" /> Build IPO DRHP's</div>
              <div className="uc-f"><i className="ti ti-folders" style={{ color: "#185FA5" }} aria-hidden="true" /> Track all your IPO Documentations in one Platform.</div>
            </div>
            <a className="uc-btn solid" href="/ipo-role-select" onClick={(e) => navClick(e, "/ipo-role-select")} data-testid="enter-workspace-btn">Enter my IPO workspace →</a>
          </div>
          <div className="uc">
            <div className="uc-h">Register as an IPO Expert</div>
            <div className="uc-who">Merchant Banker, CA/CS, CFO, Auditors, RTA, Funding Agency</div>
            <div className="uc-d">Join the Advisor Marketplace, list verified credentials and get matched to MSMEs assembling their teams.</div>
            <div className="uc-feat">
              <div className="uc-f"><i className="ti ti-rosette-discount-check" style={{ color: "#0F6E56" }} aria-hidden="true" /> SEBI-verified profile</div>
              <div className="uc-f"><i className="ti ti-users" style={{ color: "#0F6E56" }} aria-hidden="true" /> Inbound mandate matching</div>
              <div className="uc-f"><i className="ti ti-star" style={{ color: "#0F6E56" }} aria-hidden="true" /> Track record &amp; ratings</div>
            </div>
            <a className="uc-btn out" href="/advisor-onboarding" onClick={(e) => roleClick(e, "advisor", "/advisor-onboarding")} data-testid="register-advisor-btn">Register as an advisor →</a>
          </div>
          <div className="uc">
            <div className="uc-h">Register as our Partner &amp; Reseller</div>
            <div className="uc-d uc-d-1line">Earn a 25% revenue share on every IPO Mandate you bring in</div>
            <div className="uc-sub">Who can become a partner</div>
            <div className="uc-feat">
              <div className="uc-f"><i className="ti ti-circle-check" style={{ color: "#a86711" }} aria-hidden="true" /> Large CA &amp; CS Firms (who have IPO bound clients)</div>
              <div className="uc-f"><i className="ti ti-circle-check" style={{ color: "#a86711" }} aria-hidden="true" /> Large E-Commerce Platforms (who have IPO bound resellers)</div>
              <div className="uc-f"><i className="ti ti-circle-check" style={{ color: "#a86711" }} aria-hidden="true" /> Independent &amp; Fractional CFO's</div>
              <div className="uc-f"><i className="ti ti-circle-check" style={{ color: "#a86711" }} aria-hidden="true" /> Banks and NBFC's (who have IPO bound clients)</div>
            </div>
            <a className="uc-btn out" href="/reseller-network" onClick={(e) => roleClick(e, "reseller", "/reseller-network")} data-testid="register-partner-btn">Register as a partner →</a>
          </div>
        </div>
      </div>

      {/* VALUATION HOOK */}
      <div className="hook" data-testid="nul-valuation-hook">
        <div className="hook-ic" style={{ background: "#faeeda", color: "#854F0B" }}><i className="ti ti-chart-pie" aria-hidden="true" /></div>
        <div className="hook-t">
          <div className="hook-h">Curious what your company is worth — before you list?</div>
          <div className="hook-d">Run an indicative pre-IPO valuation with DCF, comparables and NAV. Instant, free and board-ready.</div>
        </div>
        <div className="hook-nums">
          <div><div className="hn-v">3 methods</div><div className="hn-l">DCF · NAV · comparable</div></div>
          <div><div className="hn-v">Instant</div><div className="hn-l">indicative range</div></div>
        </div>
        <a className="hook-btn" href="/ipo-navigator?screen=valuation" onClick={(e) => navClick(e, "/ipo-navigator?screen=valuation")} data-testid="nul-valuation">Value my business →</a>
      </div>

      {/* HOW IT WORKS */}
      <div className="sec alt">
        <div className="sec-lbl">How it works</div>
        <div className="sec-h">Four steps from private to public</div>
        <div className="steps">
          <div className="stp"><div className="stp-n">1</div><div className="stp-h">Assess readiness</div><div className="stp-d">Check SEBI eligibility and score your readiness in minutes.</div></div>
          <div className="stp"><div className="stp-n">2</div><div className="stp-h">Build your team</div><div className="stp-d">Engage verified advisors — they become owners in your DRHP.</div></div>
          <div className="stp"><div className="stp-n">3</div><div className="stp-h">Draft &amp; fund</div><div className="stp-d">Co-draft the DRHP, track costs, raise bridge &amp; pre-IPO capital.</div></div>
          <div className="stp"><div className="stp-n">4</div><div className="stp-h">File &amp; list</div><div className="stp-d">Clear SEBI review, run the roadshow, ring the bell.</div></div>
        </div>
      </div>

      {/* PLATFORM SHOWCASE */}
      <div className="sec">
        <div className="sec-lbl">See the platform</div>
        <div className="sec-h">Everything the journey needs, built in</div>
        <div className="show">
          <a className="sh" href="/ipo-navigator" onClick={(e) => navClick(e, "/ipo-navigator")} data-testid="nul-show-navigator"><div className="sh-vis" style={{ background: "#e6f1fb", color: "#0C447C" }}><i className="ti ti-route" aria-hidden="true" /></div><div className="sh-b"><div className="sh-h">IPO Navigator</div><div className="sh-d">6-stage journey tracker</div></div></a>
          <a className="sh" href="/drhp-workspace?phase=DATA_COLLECTION" onClick={(e) => navClick(e, "/drhp-workspace?phase=DATA_COLLECTION")}><div className="sh-vis" style={{ background: "#e1f5ee", color: "#085041" }}><i className="ti ti-file-text" aria-hidden="true" /></div><div className="sh-b"><div className="sh-h">DRHP Workspace</div><div className="sh-d">Co-draft with master data</div></div></a>
          <a className="sh" href="/funding" onClick={(e) => navClick(e, "/funding")}><div className="sh-vis" style={{ background: "#eef0fb", color: "#3C3489" }}><i className="ti ti-coins" aria-hidden="true" /></div><div className="sh-b"><div className="sh-h">Funding Desk</div><div className="sh-d">Pre, bridge &amp; post-IPO</div></div></a>
          <a className="sh" href="/ipo-navigator?screen=costs" onClick={(e) => navClick(e, "/ipo-navigator?screen=costs")}><div className="sh-vis" style={{ background: "#fdf3e3", color: "#a86711" }}><i className="ti ti-wallet" aria-hidden="true" /></div><div className="sh-b"><div className="sh-h">Cost Tracker</div><div className="sh-d">Every cost, every channel</div></div></a>
        </div>
      </div>

      {/* TRUST */}
      <div className="sec alt">
        <div className="trust">
          <div className="tr-i"><i className="ti ti-shield-check" aria-hidden="true" /> SEBI-aligned process</div>
          <div className="tr-i"><i className="ti ti-building-bank" aria-hidden="true" /> BSE SME &amp; NSE Emerge</div>
          <div className="tr-i"><i className="ti ti-lock" aria-hidden="true" /> Bank-grade data security</div>
          <div className="tr-i"><i className="ti ti-rosette-discount-check" aria-hidden="true" /> Verified advisors only</div>
        </div>
        <div className="tr-quote">
          <div className="tq-b">"We went in with no idea how an IPO worked. IPOLabs gave us the roadmap, the right auditor 3 km away, and a clear view of every cost. We filed in five months."</div>
          <div className="tq-a">— Founder, consumer manufacturing SME · BSE SME listing</div>
        </div>
      </div>

      {/* ORACLE */}
      <div className="oracle">
        <div className="tag" style={{ color: "#7FB0E0" }}>Oracle {"{IPO.GPT}"} · Knowledge hub</div>
        <div className="or-h">Understand the entire IPO journey — in plain language.</div>
        <div className="or-d">Ask anything about going public in India — eligibility, costs, timelines, DRHP, SEBI rules. Oracle answers with cited, India-specific sources.</div>
        <div className="or-demo">
          <div className="or-ask">
            <div className="or-in">
              <input className="or-input" placeholder="Ask Oracle anything about IPOs in India…" onKeyDown={(e) => e.key === "Enter" && navigate("/oracle")} data-testid="nul-oracle-input" />
              <a className="or-go" href="/oracle" onClick={(e) => navClick(e, "/oracle")} data-testid="nul-oracle-ask">Ask →</a>
            </div>
            <div className="or-chips">
              <a className="or-chip" href="/oracle" onClick={(e) => navClick(e, "/oracle")}>Main Board vs SME?</a>
              <a className="or-chip" href="/oracle" onClick={(e) => navClick(e, "/oracle")}>The IPO process</a>
              <a className="or-chip" href="/oracle" onClick={(e) => navClick(e, "/oracle")}>How am I valued?</a>
              <a className="or-chip" href="/oracle" onClick={(e) => navClick(e, "/oracle")}>Cost &amp; timeline</a>
            </div>
          </div>
          <div className="or-sample">
            <div className="or-q">Main Board vs SME — what's the difference?</div>
            <div className="or-a">In short: <b>SME</b> (BSE SME / NSE Emerge) suits smaller companies — lighter thresholds, post-issue capital up to ₹25 Cr, 100% underwritten. <b>Main Board</b> is for larger firms — ₹10 Cr+ post-issue capital, stricter profit and net-worth tests. Want me to check which you qualify for?</div>
          </div>
        </div>

        <a className="or-cta" href="/oracle" onClick={(e) => navClick(e, "/oracle")} data-testid="oracle-cta">
          <i className="ti ti-sparkles or-cta-ic" aria-hidden="true" />
          <div className="or-cta-1">Talk to our Oracle <span>{"{IPO LLM}"}</span></div>
          <div className="or-cta-2">Seek Clarity in any IPO Process or Step</div>
        </a>
      </div>

      {/* IPO FAQ CAROUSEL */}
      <div className="sec faqc-sec" data-testid="nul-faq-carousel">
        <div className="faqc-head">
          <div>
            <div className="sec-lbl" style={{ color: "#185FA5" }}>Knowledge Base</div>
            <div className="faqc-h">IPO FAQ&apos;s</div>
          </div>
          <a className="faqc-all" href="/ipo-faq" onClick={(e) => navClick(e, "/ipo-faq")} data-testid="nul-faq-viewall">View all FAQs <i className="ti ti-arrow-right" aria-hidden="true" /></a>
        </div>
        <div className="faqc-wrap">
          <button className="faqc-nav prev" onClick={() => scrollFaq(-1)} aria-label="Previous FAQs" data-testid="nul-faq-prev"><i className="ti ti-chevron-left" aria-hidden="true" /></button>
          <div className="faqc-track" ref={faqRef}>
            {FAQ_PREVIEW.map((f, i) => (
              <a className="faqc-card" key={i} href="/ipo-faq" onClick={(e) => navClick(e, "/ipo-faq")} data-testid={`nul-faq-card-${i}`}>
                <div className="faqc-cat">{f.cat}</div>
                <div className="faqc-q">{f.q}</div>
                <div className="faqc-read">Read answer <i className="ti ti-arrow-right" aria-hidden="true" /></div>
              </a>
            ))}
          </div>
          <button className="faqc-nav next" onClick={() => scrollFaq(1)} aria-label="Next FAQs" data-testid="nul-faq-next"><i className="ti ti-chevron-right" aria-hidden="true" /></button>
        </div>
      </div>

      {/* PARTNER STRIP */}
      <div className="partner-strip">
        <div className="ps-ic"><i className="ti ti-world-share" aria-hidden="true" /></div>
        <div className="ps-t">
          <div className="ps-h">Refer SMEs to IPO — earn 25% revenue share</div>
          <div className="ps-d">For CA firms, fractional CFOs, e-commerce platforms and banks. Pan-India referral network.</div>
        </div>
        <a className="ps-btn" href="/reseller-network" onClick={(e) => roleClick(e, "reseller", "/reseller-network")} data-testid="nul-become-partner">Become a partner →</a>
      </div>
    </div>
  );
};

export default NewUserLanding;
